import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { tileRenderer } from './tileRenderer';
import { modelLoader } from './modelLoader';
import WorldMap from './worldmap';
import tunnelAlgorithm from './tunnelAlgorithm';
import { AudioManager } from './audioManager';

// Global variables
let scene: THREE.Scene;
let camera: THREE.PerspectiveCamera;
let renderer: THREE.WebGLRenderer;
let controls: OrbitControls;
const CAMERA_FOV = 60;
let worldMap: WorldMap;
let dungeonMeshes: THREE.Object3D[] = [];
let torchLights: THREE.PointLight[] = [];
let animationFrameId: number | null = null;
let audioManager: AudioManager;
let clock: THREE.Clock;
let vignetteOverlay: THREE.Mesh;
let vignetteEnabled = true;
let isDisposed = false;
const AMBIENT_LIGHT_COLOR = 0xFAE9D5;
const AMBIENT_LIGHT_INTENSITY = 0.2;
const SHADOW_MAP_SIZE = 1024;
const FADE_DURATION = 1; // Duration in seconds for fade-in (changed from 2 to 1)
let fadeStartTime: number | null = null;
let isTransitioningOut = false;
const TRANSITION_DURATION = 1; // Duration in seconds
let transitionStartTime: number | null = null;
const ZOOM_DISTANCE = 10;
const initialCameraPosition = new THREE.Vector3(0, 1.3, 1.7);
const targetCameraPosition = new THREE.Vector3(0, 1.3, -8.3); // Move forward 10 units

/**
 * Initialize the welcome screen
 */
export async function initWelcomeScreen(container: HTMLElement): Promise<void> {
    // Create needed objects
    clock = new THREE.Clock();
    scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    
    // Set up camera
    camera = new THREE.PerspectiveCamera(CAMERA_FOV, container.clientWidth / container.clientHeight, 0.1, 100);
    camera.position.set(0, 1.3, 1.7); // Position camera to look at origin from distance of 10
    camera.lookAt(0, 1.2, 0);
    
    // Initialize audio manager and wait for sounds to load
    audioManager = AudioManager.getInstance();
    await audioManager.init();
    
    // Set up renderer
    renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.shadowMap.enabled = true;
    renderer.outputEncoding = THREE.LinearEncoding;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    container.appendChild(renderer.domElement);
    
    // Add fog
    scene.fog = new THREE.FogExp2(0x000000, 0.05);
    
    // Load models
    if (modelLoader) {
        try {
            await modelLoader.loadModels();
            if (tileRenderer) {
                tileRenderer.init();
            }
        } catch (error) {
            console.error("Failed to load models:", error);
        }
    }
    
    // Generate the dungeon using TunnelAlgorithm
    generateDungeon();
    
    // Add lights
    addLights();
    
    setupVignetteOverlay();
    
    // Initialize light fade-in
    fadeStartTime = clock.getElapsedTime();
    
    // Start animation
    animate();
}

/**
 * Generate dungeon using TunnelAlgorithm
 */
function generateDungeon(): void {
    // Make sure we have access to the TunnelAlgorithm
    if (!tunnelAlgorithm) {
        console.error("TunnelAlgorithm not found");
        return;
    }
    
    // Initialize the TunnelAlgorithm
    const result = tunnelAlgorithm.initialize();
    worldMap = result.worldMap;
    
    // Dig tunnels in 4 directions from (0,0)
    const directions = [
        { x: 0, z: -1 }, // North
        { x: 1, z: 0 },  // East
        { x: 0, z: 1 },  // South
        { x: -1, z: 0 }  // West
    ];
    
    // Dig a 10-length tunnel in each direction
    directions.forEach(direction => {
        tunnelAlgorithm.digTunnel(0, 0, direction, 10);
    });
    
    // Render tiles
    renderTiles();
}

/**
 * Add lights to the scene
 */
function addLights(): void {
    // Basic ambient light - start at 0 intensity
    const ambientLight = new THREE.AmbientLight(AMBIENT_LIGHT_COLOR, 0);
    ambientLight.name = "ambientLight";
    scene.add(ambientLight);
    
    // Add torch lights at key positions - start at 0 intensity
    const torchPositions = [
        { x: -2, z: -1 },
        { x: 2, z: -1 },
    ];
    
    torchPositions.forEach(pos => {
        const torch = new THREE.PointLight(tileRenderer.TORCH_LIGHT_COLOR, 0, 10, 0.5);
        torch.position.set(pos.x, 2, pos.z);
        torch.castShadow = true;
        torch.shadow.bias = -0.0005;
        torch.shadow.mapSize = new THREE.Vector2(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE);
        torch.userData = {
            isTorch: true,
            originalIntensity: tileRenderer.TORCH_LIGHT_INTENSITY,
            flickerSpeed: 1 + Math.random() * 0.1,
            flickerAmount: 0.2 + Math.random() * 0.2,
            flickerPhase: Math.random() * Math.PI * 2,
            originalColor: torch.color.clone(),
            hueShift: Math.random() * 0.01,
        };
        scene.add(torch);
        torchLights.push(torch);
    });
}

function applyFlickering(light: THREE.Light, time: number, fadeMultiplier: number): void {
    const { originalIntensity, flickerSpeed, flickerAmount, flickerPhase, hueShift } = light.userData;
    if (!originalIntensity) return;

    // Calculate flickering effect
    // Use multiple sine waves with different frequencies for more natural flickering
    const t = time; // Convert to seconds
    const flicker1 = Math.sin((t + flickerPhase) * flickerSpeed * 5) * 0.2;
    const flicker2 = Math.sin((t + flickerPhase) * flickerSpeed * 7.3) * 0.2;
    const flicker3 = Math.sin((t + flickerPhase) * flickerSpeed * 11.9) * 0.1;

    // Combine the sine waves and scale by the flicker amount
    const flickerFactor = 1 + (flicker1 + flicker2 + flicker3) * flickerAmount * 0.44;

    // Apply intensity variation with fade-in factor
    light.intensity = originalIntensity * flickerFactor * fadeMultiplier;

    // Apply subtle color variation (warm to warmer)
    const hue = 0.3 * Math.sin(t * 2 + flickerPhase) * hueShift + 0.08; // Base orange-yellow
    const lightness = 0.5 + Math.sin(t * 5 + flickerPhase) * 0.02;

    // Apply the color
    if (light.color && light.color.setHSL) {
        light.color.setHSL(hue, 0.7, lightness);
    }
}

/**
 * Render all tiles in the world map
 */
function renderTiles(): void {
    if (!tileRenderer) return;
    
    // Get tiles to render from TunnelAlgorithm
    const tilesToRender = tunnelAlgorithm.getTilesToRender(0, 0, 12, 12);
    
    // Convert Map to array for iteration
    for (const [key, tileType] of tilesToRender.entries()) {
        const [x, z] = key.split(',').map(Number);
        
        // Basic geometries for fallback
        const geometries = {
            floor: new THREE.PlaneGeometry(1, 1),
            wall: new THREE.BoxGeometry(1, 1, 1)
        };
        
        const materials = {
            floor: new THREE.MeshStandardMaterial({ color: 0x555555 }),
            wall: new THREE.MeshStandardMaterial({ color: 0x333333 })
        };
        let forceTorch = false;
        if (x === -2 && z === -1 || x === 2 && z === -1) {
            forceTorch = true;
        }
        try {
            const tileMesh = tileRenderer.createTileMesh(x, z, tileType, geometries, materials, 1, forceTorch);
            if (tileMesh) {
                scene.add(tileMesh);
                dungeonMeshes.push(tileMesh);
            }
        } catch (error) {
            console.error(`Error creating tile at ${x},${z}:`, error);
        }
    }
}

/**
 * Animation loop
 */
function animate(): void {
    const time = clock.getElapsedTime();
    
    // Calculate fade-in progress
    let fadeMultiplier = 1.0;
    if (fadeStartTime !== null) {
        const fadeProgress = (time - fadeStartTime) / FADE_DURATION;
        fadeMultiplier = Math.min(fadeProgress, 1.0); // Clamp between 0 and 1
    }
    
    // Handle transition out animation
    if (isTransitioningOut && transitionStartTime !== null) {
        const progress = Math.min((time - transitionStartTime) / TRANSITION_DURATION, 1);
        
        // Update camera position
        const newPosition = new THREE.Vector3();
        newPosition.lerpVectors(initialCameraPosition, targetCameraPosition, progress);
        camera.position.copy(newPosition);
        
        // Update overlay opacity
        const overlay = scene.getObjectByName('transitionOverlay') as THREE.Mesh;
        if (overlay && overlay.material instanceof THREE.MeshBasicMaterial) {
            overlay.material.opacity = progress;
        }
    }
    
    // Update ambient light with fade
    const ambientLight = scene.getObjectByName("ambientLight") as THREE.AmbientLight;
    if (ambientLight) {
        ambientLight.intensity = AMBIENT_LIGHT_INTENSITY * fadeMultiplier;
    }
    
    // Update torch lights with fade and flickering
    torchLights.forEach(light => {
        applyFlickering(light, time, fadeMultiplier);
    });
    
    // Render the scene
    renderer.render(scene, camera);
    
    // Request next frame if not disposed
    if (!isDisposed) {
        animationFrameId = requestAnimationFrame(animate);
    }

    animateFlame(time);
}

/**
 * Handle window resize
 */
export function resizeWelcomeScreen(width: number, height: number): void {
    if (camera && renderer) {
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);
        // Update vignette dimensions when window is resized
        if (vignetteOverlay) {
            // Get the distance from camera
            const distance = Math.abs(vignetteOverlay.position.z);

            // Recalculate dimensions
            const aspectRatio = window.innerWidth / window.innerHeight;
            const vFOV = CAMERA_FOV * Math.PI / 180;
            const height = 2 * distance * Math.tan(vFOV / 2);
            const width = height * aspectRatio;

            // Create a new geometry with updated dimensions
            vignetteOverlay.geometry.dispose(); // Clean up old geometry
            vignetteOverlay.geometry = new THREE.PlaneGeometry(width, height);
        }
    }
}
function setupVignetteOverlay(): void {
    // Load the vignette texture
    const textureLoader = new THREE.TextureLoader();
    textureLoader.load('Vignette-PNG-Images.png', function (texture) {
        // Calculate the correct size of the plane based on the camera's FOV and distance
        const distance = 10; // Distance from camera to plane
        const aspectRatio = window.innerWidth / window.innerHeight;

        // Calculate height based on vertical FOV (in radians)
        const vFOV = CAMERA_FOV * Math.PI / 180; // Convert degrees to radians
        const height = 2 * distance * Math.tan(vFOV / 2);

        // Width is proportional to the height by the aspect ratio
        const width = height * aspectRatio;

        // Create a plane geometry with the calculated dimensions
        const planeGeometry = new THREE.PlaneGeometry(width, height);

        // Create a transparent material with the vignette texture
        const material = new THREE.MeshBasicMaterial({
            map: texture,
            transparent: true,
            depthTest: false,
            depthWrite: false,
            opacity: 0.7,
        });

        // Create the mesh for the vignette
        vignetteOverlay = new THREE.Mesh(planeGeometry, material);
        vignetteOverlay.renderOrder = 9999; // Ensure it renders last
        vignetteOverlay.frustumCulled = false; // Never cull this object

        // Add it directly to the camera
        camera.add(vignetteOverlay);

        // Position it at the calculated distance in front of the camera
        vignetteOverlay.position.set(0, 0, -distance);

        // Set visible based on current setting
        vignetteOverlay.visible = vignetteEnabled;

        // Add the camera to the scene (if not already added)
        scene.add(camera);

        
    }, undefined, function (err) {
        console.error("Error loading vignette texture:", err);
    });
}

function animateFlame(time: number) {
    
    dungeonMeshes.forEach(mesh => {
        let torchFlame = mesh.getObjectByName("Fire_LPolyPalete_0");
        if (!torchFlame) return;
        const flickerPhase = torchFlame.userData.flickerPhase;
        const t = time;
        const t1 = 0.001 * Math.abs(Math.sin(5*t + flickerPhase)) + 0.002;
        const t2 = 0.002 * Math.abs(Math.sin(7*t + flickerPhase)) + 0.002;
        const t3 = 0.002 * Math.abs(Math.sin(11*t + flickerPhase)) + 0.002;
        torchFlame.scale.z = (t1 + t2 + t3)/3;
    });
}

/**
 * Clean up resources
 */
export function disposeWelcomeScreen(): void {
    // Stop animation
    if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
    }
    
    // Dispose of controls
    if (controls) {
        controls.dispose();
    }
    
    // Clean up meshes
    dungeonMeshes.forEach(mesh => {
        scene.remove(mesh);
        disposeMesh(mesh);
    });
    
    // Clean up lights
    torchLights.forEach(light => {
        scene.remove(light);
    });
    
    // Remove renderer
    if (renderer && renderer.domElement.parentNode) {
        renderer.domElement.parentNode.removeChild(renderer.domElement);
    }
}
/**
 * Helper to dispose mesh resources
 */
function disposeMesh(mesh: THREE.Object3D): void {
    mesh.traverse(child => {
        if (child instanceof THREE.Mesh) {
            if (child.geometry) {
                child.geometry.dispose();
            }
            
            if (child.material) {
                if (Array.isArray(child.material)) {
                    child.material.forEach(material => material.dispose());
                } else {
                    child.material.dispose();
                }
            }
        }
    });
}

/**
 * Start the transition out animation
 */
export function startTransitionOut(): void {
    audioManager.playSound(AudioManager.SOUNDS.WelcomeZoom);
    isTransitioningOut = true;
    transitionStartTime = clock.getElapsedTime();
    
    // Create black overlay for fade effect if it doesn't exist
    if (!scene.getObjectByName('transitionOverlay')) {
        const overlayGeometry = new THREE.PlaneGeometry(2, 2);
        const overlayMaterial = new THREE.MeshBasicMaterial({
            color: 0x000000,
            transparent: true,
            opacity: 0,
            depthTest: false
        });
        const overlay = new THREE.Mesh(overlayGeometry, overlayMaterial);
        overlay.name = 'transitionOverlay';
        overlay.position.z = -1;
        camera.add(overlay);
        scene.add(camera);
    }
} 